import api from "../api";
import type { User } from "../auth";

export interface Group {
  id: string;
  name: string;
  description: string;
  slug: string;
  imageUrl?: string;
  coverImage?: string;
  type: "public" | "private" | "secret";
  category: string;
  memberCount: number;
  postCount: number;
  owner: User;
  admins: User[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateGroupData {
  name: string;
  description: string;
  type: string;
  category?: string;
  image?: File;
  coverImage?: string;
}

const groupService = {
  getGroups: async (
    params: {
      page?: number;
      limit?: number;
      category?: string;
      type?: Group["type"];
      search?: string;
    } = {},
  ): Promise<{ groups: Group[]; total: number }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value.toString());
    });

    const response = await api.get<{ groups: Group[]; total: number }>(
      `groups?${queryParams.toString()}`,
    );
    return response.data;
  },

  getGroup: async (idOrSlug: string): Promise<Group> => {
    const response = await api.get<{ group: Group }>(`groups/${idOrSlug}`);
    return response.data.group;
  },

  createGroup: async (data: CreateGroupData): Promise<Group> => {
    const formData = new FormData();
    if (data.image instanceof File) {
      formData.append("image", data.image);
    }
    const groupData = { ...data };
    delete groupData.image;
    formData.append("data", JSON.stringify(groupData));

    const response = await api.post<{ group: Group }>("groups", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.group;
  },

  updateGroup: async (
    groupId: string,
    data: Partial<CreateGroupData>,
  ): Promise<Group> => {
    const response = await api.put<{ group: Group }>(`groups/${groupId}`, data);
    return response.data.group;
  },

  deleteGroup: async (groupId: string): Promise<void> => {
    await api.delete(`groups/${groupId}`);
  },

  joinGroup: async (groupId: string): Promise<void> => {
    await api.post(`groups/${groupId}/join`);
  },

  leaveGroup: async (groupId: string): Promise<void> => {
    await api.delete(`groups/${groupId}/leave`);
  },

  getGroupMembers: async (
    groupId: string,
    params: {
      page?: number;
      limit?: number;
      role?: "all" | "admin" | "member";
    } = {},
  ): Promise<{ users: User[]; total: number }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value.toString());
    });

    const response = await api.get<{ users: User[]; total: number }>(
      `groups/${groupId}/members?${queryParams.toString()}`,
    );
    return response.data;
  },

  searchGroups: async (params: {
    query: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get("groups/search", {
      params,
    });
    return response.data;
  },
};

export default groupService;
